import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Wallet as WalletIcon, User, Bitcoin, 
  Lock, Unlock, Calendar, BarChart3, History,
  Copy, Eye, Download, RefreshCw,
  Shield, AlertCircle, CheckCircle, XCircle,
  Activity, Users, ExternalLink,
  ChevronLeft, ChevronRight, TrendingUp, TrendingDown,
  DollarSign, Target, PieChart, CreditCard,
  ShieldCheck, Smartphone, Zap, MoreVertical,
  FileText, Shield as ShieldIcon, Globe,
  Upload, Download as DownloadIcon, Send,
  Filter, Search, Plus, Minus, Clock,
} from 'lucide-react';
import { AdminWalletAPI, AdminAPIUtils } from '../services/api';

function WalletDetail() {
  const { walletId } = useParams();
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [txLoading, setTxLoading] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustDescription, setAdjustDescription] = useState('');
  const [adjustLoading, setAdjustLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [transactionType, setTransactionType] = useState('all');
  const [actionMenu, setActionMenu] = useState(null);
  const [transactionPagination, setTransactionPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1
  });

  useEffect(() => {
    fetchWallet();
  }, [walletId]);

  useEffect(() => {
    if (wallet) {
      if (activeTab === 'transactions') {
        fetchTransactions();
      } else if (activeTab === 'logs') {
        fetchLogs();
      }
    }
  }, [activeTab, transactionPagination.page, transactionType, wallet]);

  const fetchWallet = async () => {
    try {
      setLoading(true);
      const data = await AdminWalletAPI.getWallet(walletId);
      setWallet(data);
    } catch (err) {
      setError(AdminAPIUtils.handleError(err, false).message);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      setTxLoading(true);
      const params = {
        page: transactionPagination.page,
        limit: transactionPagination.limit,
        wallet_id: walletId
      };

      if (transactionType !== 'all') {
        params.type = transactionType;
      }

      if (searchTerm) {
        params.search = searchTerm;
      }

      const response = await AdminWalletAPI.getTransactions(params);
      setTransactions(response.transactions || []);
      setTransactionPagination({
        page: response.pagination?.page || 1,
        limit: response.pagination?.limit || 10,
        total: response.pagination?.total || 0,
        totalPages: response.pagination?.pages || 1
      });
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
    } finally {
      setTxLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      setLogsLoading(true);
      const response = await AdminWalletAPI.getWalletLogs(walletId, {
        page: 1,
        limit: 20
      });
      setLogs(response.logs || []);
    } catch (err) {
      console.error('Failed to fetch logs:', err);
    } finally {
      setLogsLoading(false);
    }
  };

  const toggleWalletLock = async () => {
    const action = wallet.locked ? 'unlock' : 'lock';
    const reason = prompt(`Enter reason to ${action} this wallet:`);
    
    if (!reason) return;
    
    try {
      await AdminWalletAPI.toggleWalletLock(walletId, !wallet.locked, reason);
      fetchWallet(); // Refresh wallet data
      alert(`Wallet ${action}ed successfully`);
    } catch (err) {
      AdminAPIUtils.handleError(err);
    }
  };

  const handleAdjustBalance = async (e) => {
    e.preventDefault();
    
    if (!adjustAmount || !adjustDescription) {
      alert('Please fill all fields');
      return;
    }

    const amount = parseFloat(adjustAmount);
    if (isNaN(amount) || amount === 0) {
      alert('Please enter a valid amount');
      return;
    }

    const confirmMessage = `Are you sure you want to ${amount > 0 ? 'add' : 'deduct'} ${AdminAPIUtils.formatBTC(Math.abs(amount))} BTC from this wallet?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setAdjustLoading(true);
      await AdminWalletAPI.adjustBalance(walletId, amount, adjustDescription);
      
      alert('Balance adjusted successfully');
      setAdjustAmount('');
      setAdjustDescription('');
      fetchWallet();
      if (activeTab === 'transactions') {
        fetchTransactions();
      }
    } catch (err) {
      AdminAPIUtils.handleError(err);
    } finally {
      setAdjustLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const getWalletTypeBadge = (type) => {
    const config = {
      bitcoin: { bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/20', icon: <Bitcoin className="w-3 h-3" /> },
      fiat: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20', icon: <DollarSign className="w-3 h-3" /> },
      bonus: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/20', icon: <ShieldCheck className="w-3 h-3" /> },
      game: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20', icon: <Activity className="w-3 h-3" /> }
    };
    
    const configItem = config[type] || { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/20', icon: <WalletIcon className="w-3 h-3" /> };
    
    return (
      <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${configItem.bg} ${configItem.text} ${configItem.border} flex items-center gap-1.5`}>
        {configItem.icon}
        {type?.charAt(0).toUpperCase() + type?.slice(1)} Wallet
      </span>
    );
  };

  const getTransactionTypeBadge = (type) => {
    const config = {
      deposit: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20', icon: <DownloadIcon className="w-3 h-3" /> },
      withdrawal: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20', icon: <Upload className="w-3 h-3" /> },
      bet: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20', icon: <Target className="w-3 h-3" /> },
      win: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/20', icon: <TrendingUp className="w-3 h-3" /> },
      adjust: { bg: 'bg-sky-500/10', text: 'text-sky-400', border: 'border-sky-500/20', icon: <RefreshCw className="w-3 h-3" /> }
    };
    
    const configItem = config[type] || { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/20', icon: <Activity className="w-3 h-3" /> };
    
    return (
      <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${configItem.bg} ${configItem.text} ${configItem.border} flex items-center gap-1.5`}>
        {configItem.icon}
        {type?.charAt(0).toUpperCase() + type?.slice(1)}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const config = {
      1: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20', icon: <CheckCircle className="w-3 h-3" /> },
      0: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20', icon: <Clock className="w-3 h-3" /> },
      2: { bg: 'bg-rose-500/10', text: 'text-rose-400', border: 'border-rose-500/20', icon: <XCircle className="w-3 h-3" /> },
      3: { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/20', icon: <XCircle className="w-3 h-3" /> }
    };
    
    const labels = {
      1: 'Completed',
      0: 'Pending',
      2: 'Failed',
      3: 'Cancelled'
    };
    
    const configItem = config[status] || config[3];
    
    return (
      <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${configItem.bg} ${configItem.text} ${configItem.border} flex items-center gap-1.5`}>
        {configItem.icon}
        {labels[status] || 'Unknown'}
      </span>
    );
  };

  const getLockStatus = (locked) => {
    return locked ? (
      <span className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-500/10 text-rose-400 border border-rose-500/20 flex items-center gap-1.5">
        <Lock className="w-3 h-3" />
        Locked
      </span>
    ) : (
      <span className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center gap-1.5">
        <Unlock className="w-3 h-3" />
        Active
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-deep-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-sky-500 mx-auto mb-6"></div>
          <p className="text-charcoal-gray">Loading wallet details...</p>
        </div>
      </div>
    );
  }

  if (error || !wallet) {
    return (
      <div className="flex justify-center items-center h-screen bg-deep-black">
        <div className="text-center">
          <AlertCircle className="w-20 h-20 text-rose-400 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-white mb-4">
            {error ? 'Error Loading Wallet' : 'Wallet Not Found'}
          </h2>
          <p className="text-charcoal-gray mb-8 max-w-md mx-auto">
            {error || 'The wallet you are looking for does not exist or has been removed.'}
          </p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => navigate('/wallet')}
              className="px-6 py-3 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300 hover:scale-105"
            >
              Back to Wallets
            </button>
            <button
              onClick={fetchWallet}
              className="px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all duration-300"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Available Balance',
      value: `${AdminAPIUtils.formatBTC(wallet.balance || 0)} BTC`,
      icon: <WalletIcon className="w-5 h-5" />,
      color: 'from-emerald-500 to-green-500',
      change: '2.5%',
      changeText: 'from yesterday',
      trend: 'up'
    },
    {
      title: 'Locked Balance',
      value: `${AdminAPIUtils.formatBTC(wallet.locked_balance || 0)} BTC`,
      icon: <Lock className="w-5 h-5" />,
      color: 'from-rose-500 to-red-500',
      change: '1.2 BTC',
      changeText: 'in pending tx',
      trend: 'neutral'
    },
    {
      title: 'Total Transactions',
      value: wallet.transaction_stats?.total_transactions || 0,
      icon: <Activity className="w-5 h-5" />,
      color: 'from-blue-500 to-cyan-500',
      change: '12',
      changeText: 'this month',
      trend: 'up'
    },
    {
      title: 'Total Deposits',
      value: `${AdminAPIUtils.formatBTC(wallet.transaction_stats?.total_deposits || 0)} BTC`,
      icon: <DownloadIcon className="w-5 h-5" />,
      color: 'from-purple-500 to-violet-500',
      change: `${wallet.transaction_stats?.deposit_count || 0} deposits`,
      changeText: 'all time',
      trend: 'up'
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/wallet')}
            className="p-2 bg-gray-800/50 hover:bg-gray-700 text-charcoal-gray hover:text-white rounded-xl transition-all duration-300 hover:scale-105"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-sky-400 to-blue-500 bg-clip-text text-transparent">
              Wallet Details
            </h1>
            <p className="text-charcoal-gray mt-1">Manage and monitor wallet transactions</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate(`/users/${wallet.user_id}`)}
            className="px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all duration-300 hover:scale-[1.02] flex items-center gap-2"
          >
            <User className="w-4 h-4" />
            View User
          </button>
          <button
            onClick={toggleWalletLock}
            className={`px-4 py-2.5 rounded-xl transition-all duration-300 hover:scale-[1.02] flex items-center gap-2 ${
              wallet.locked
                ? 'bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white'
                : 'bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-600 hover:to-red-700 text-white'
            }`}
          >
            {wallet.locked ? (
              <>
                <Unlock className="w-4 h-4" />
                Unlock Wallet
              </>
            ) : (
              <>
                <Lock className="w-4 h-4" />
                Lock Wallet
              </>
            )}
          </button>
          <button
            onClick={() => setActiveTab('adjust')}
            className="px-4 py-2.5 bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-white rounded-xl transition-all duration-300 hover:scale-[1.02] flex items-center gap-2"
          >
            <Bitcoin className="w-4 h-4" />
            Adjust Balance
          </button>
          <button
            onClick={fetchWallet}
            className="p-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all duration-300 hover:scale-105"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Wallet Header */}
      <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
        <div className="flex flex-col lg:flex-row lg:items-start gap-6">
          <div className="flex-shrink-0">
            <div className="relative">
              <div className={`h-24 w-24 rounded-2xl flex items-center justify-center text-white font-bold text-3xl shadow-xl ${
                wallet.wallet_type === 'bitcoin' ? 'bg-gradient-to-br from-orange-500 to-amber-600' :
                wallet.wallet_type === 'fiat' ? 'bg-gradient-to-br from-emerald-500 to-green-600' :
                'bg-gradient-to-br from-blue-500 to-cyan-600'
              }`}>
                {wallet.wallet_type === 'bitcoin' ? (
                  <Bitcoin className="h-10 w-10" />
                ) : wallet.wallet_type === 'fiat' ? (
                  <DollarSign className="h-10 w-10" />
                ) : (
                  <WalletIcon className="h-10 w-10" />
                )}
              </div>
              <div className={`absolute -bottom-2 -right-2 w-8 h-8 rounded-full border-4 border-night-blue flex items-center justify-center ${
                wallet.locked ? 'bg-rose-500' : 'bg-emerald-500'
              }`}>
                {wallet.locked ? (
                  <Lock className="w-4 h-4 text-white" />
                ) : (
                  <Unlock className="w-4 h-4 text-white" />
                )}
              </div>
            </div>
          </div>
          <div className="flex-1">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-white">
                  {wallet.wallet_type === 'bitcoin' ? 'Bitcoin Wallet' : 
                   wallet.wallet_type === 'fiat' ? 'Fiat Wallet' : 
                   'Game Wallet'}
                </h2>
                <div className="flex flex-wrap items-center gap-4 mt-2">
                  <div className="flex items-center gap-2 text-charcoal-gray">
                    <WalletIcon className="w-4 h-4" />
                    <span>ID: {wallet.id}</span>
                    <button
                      onClick={() => copyToClipboard(wallet.id)}
                      className="text-charcoal-gray hover:text-white transition-colors p-1 hover:bg-gray-800/50 rounded-lg"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="flex items-center gap-2 text-charcoal-gray">
                    <User className="w-4 h-4" />
                    <span>{wallet.username}</span>
                    <button
                      onClick={() => navigate(`/users/${wallet.user_id}`)}
                      className="text-charcoal-gray hover:text-white transition-colors p-1 hover:bg-gray-800/50 rounded-lg"
                    >
                      <Eye className="w-3 h-3" />
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3 mt-4">
                  {getWalletTypeBadge(wallet.wallet_type)}
                  {getLockStatus(wallet.locked)}
                  {wallet.bitcoin_address && (
                    <button
                      onClick={() => copyToClipboard(wallet.bitcoin_address)}
                      className="px-3 py-1.5 bg-gray-800/50 hover:bg-gray-700 text-gray-300 hover:text-white rounded-lg transition-all duration-300 flex items-center gap-1.5"
                    >
                      <Bitcoin className="w-3 h-3" />
                      Copy Address
                    </button>
                  )}
                  <span className="text-sm text-charcoal-gray">
                    Created {AdminAPIUtils.formatDate(wallet.created_at, 'date')}
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                <div className="text-right">
                  <div className="text-3xl font-bold bg-gradient-to-r from-emerald-400 to-green-500 bg-clip-text text-transparent">
                    {AdminAPIUtils.formatBTC(wallet.balance || 0)} BTC
                  </div>
                  <div className="text-sm text-charcoal-gray mt-1">Available Balance</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card, index) => (
          <div 
            key={index} 
            className="bg-night-blue/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-6 hover:bg-night-blue/70 transition-all duration-300 hover:scale-[1.02] group"
          >
            <div className="flex items-start justify-between mb-6">
              <div className={`p-3 rounded-xl bg-gradient-to-br ${card.color} shadow-lg`}>
                {card.icon}
              </div>
              {card.trend === 'up' && (
                <div className="flex items-center text-emerald-400 bg-emerald-400/10 px-3 py-1 rounded-full">
                  <TrendingUp className="w-4 h-4 mr-1" />
                  <span className="text-sm font-medium">{card.change}</span>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <p className="text-3xl font-bold text-white">{card.value}</p>
              <p className="text-charcoal-gray text-sm uppercase tracking-wider">{card.title}</p>
            </div>
            <div className="mt-6 pt-4 border-t border-gray-800">
              <p className="text-sm text-charcoal-gray">
                <span className={`font-medium ${card.trend === 'up' ? 'text-emerald-400' : 'text-gray-400'}`}>
                  {card.change}
                </span>{' '}
                {card.changeText}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Content */}
        <div className="lg:col-span-2">
          {/* Tabs */}
          <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl overflow-hidden">
            <div className="border-b border-gray-800">
              <nav className="flex overflow-x-auto">
                {['overview', 'transactions', 'logs', 'adjust'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-6 py-4 font-medium text-sm whitespace-nowrap transition-colors relative ${
                      activeTab === tab
                        ? 'text-white border-b-2 border-sky-500'
                        : 'text-charcoal-gray hover:text-white hover:bg-gray-800/30'
                    }`}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Wallet Information */}
                  <div>
                    <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                      <WalletIcon className="w-5 h-5" />
                      Wallet Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-charcoal-gray mb-2">User Information</label>
                          <div className="flex items-center gap-3 p-3 bg-gray-800/30 rounded-xl">
                            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
                              {wallet.username?.charAt(0).toUpperCase() || 'U'}
                            </div>
                            <div>
                              <div className="font-medium text-white">{wallet.username}</div>
                              <div className="text-sm text-charcoal-gray">{wallet.email}</div>
                            </div>
                            <button
                              onClick={() => navigate(`/users/${wallet.user_id}`)}
                              className="ml-auto p-2 bg-gray-800/50 hover:bg-gray-700 text-charcoal-gray hover:text-white rounded-lg transition-colors"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-charcoal-gray mb-2">Wallet Status</label>
                          <div className="flex items-center gap-3">
                            {getLockStatus(wallet.locked)}
                            <span className="text-sm text-white">
                              {wallet.locked_reason ? wallet.locked_reason : 'No lock reason'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-charcoal-gray mb-2">Bitcoin Address</label>
                          {wallet.bitcoin_address ? (
                            <div className="flex items-center gap-2 p-3 bg-gray-800/30 rounded-xl">
                              <Bitcoin className="w-4 h-4 text-amber-400" />
                              <code className="text-sm text-gray-300 truncate flex-1">{wallet.bitcoin_address}</code>
                              <button
                                onClick={() => copyToClipboard(wallet.bitcoin_address)}
                                className="text-charcoal-gray hover:text-white transition-colors p-1 hover:bg-gray-800/50 rounded-lg"
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                              <a
                                href={`https://blockchain.info/address/${wallet.bitcoin_address}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sky-400 hover:text-sky-300 transition-colors p-1 hover:bg-gray-800/50 rounded-lg"
                              >
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </div>
                          ) : (
                            <div className="text-charcoal-gray italic p-3 bg-gray-800/30 rounded-xl">
                              No Bitcoin address assigned
                            </div>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-charcoal-gray mb-2">Created</label>
                            <div className="text-white">
                              {AdminAPIUtils.formatDate(wallet.created_at, 'datetime')}
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-charcoal-gray mb-2">Last Activity</label>
                            <div className="text-white">
                              {wallet.last_activity ? AdminAPIUtils.formatDate(wallet.last_activity, 'datetime') : 'Never'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Balance Breakdown */}
                  <div>
                    <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                      <PieChart className="w-5 h-5" />
                      Balance Breakdown
                    </h3>
                    <div className="space-y-4">
                      <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                        <div className="flex h-full">
                          <div 
                            className="bg-gradient-to-r from-emerald-500 to-green-600" 
                            style={{ width: `${(parseFloat(wallet.balance || 0) / ((parseFloat(wallet.balance || 0) + parseFloat(wallet.locked_balance || 0)) || 1) * 100)}%` }}
                          ></div>
                          <div 
                            className="bg-gradient-to-r from-rose-500 to-red-600" 
                            style={{ width: `${(parseFloat(wallet.locked_balance || 0) / ((parseFloat(wallet.balance || 0) + parseFloat(wallet.locked_balance || 0)) || 1) * 100)}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 bg-gray-800/30 rounded-xl">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                              <span className="text-charcoal-gray">Available Balance</span>
                            </div>
                            <span className="text-lg font-bold text-emerald-400">
                              {AdminAPIUtils.formatBTC(wallet.balance || 0)} BTC
                            </span>
                          </div>
                          <p className="text-xs text-charcoal-gray">Funds ready for use</p>
                        </div>
                        <div className="p-4 bg-gray-800/30 rounded-xl">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-rose-500"></div>
                              <span className="text-charcoal-gray">Locked Balance</span>
                            </div>
                            <span className="text-lg font-bold text-rose-400">
                              {AdminAPIUtils.formatBTC(wallet.locked_balance || 0)} BTC
                            </span>
                          </div>
                          <p className="text-xs text-charcoal-gray">Funds in pending transactions</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'transactions' && (
                <div>
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      <Activity className="w-5 h-5" />
                      Recent Transactions
                    </h3>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-charcoal-gray w-4 h-4" />
                        <input
                          type="text"
                          placeholder="Search transactions..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && fetchTransactions()}
                          className="pl-10 pr-4 py-2 bg-gray-900/50 border border-gray-800 rounded-xl text-white placeholder-charcoal-gray focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors w-full sm:w-64"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Filter className="text-charcoal-gray w-4 h-4" />
                        <select
                          value={transactionType}
                          onChange={(e) => setTransactionType(e.target.value)}
                          className="px-4 py-2 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                        >
                          <option value="all">All Types</option>
                          <option value="deposit">Deposits</option>
                          <option value="withdrawal">Withdrawals</option>
                          <option value="bet">Bets</option>
                          <option value="win">Wins</option>
                          <option value="adjust">Adjustments</option>
                        </select>
                        <button
                          onClick={fetchTransactions}
                          className="p-2 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all duration-300 hover:scale-105"
                        >
                          <RefreshCw className={`w-4 h-4 ${txLoading ? 'animate-spin' : ''}`} />
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {txLoading ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sky-500 mx-auto mb-6"></div>
                      <p className="text-charcoal-gray">Loading transactions...</p>
                    </div>
                  ) : transactions.length === 0 ? (
                    <div className="text-center py-12">
                      <History className="w-16 h-16 text-gray-700 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-white mb-2">No transactions found</h3>
                      <p className="text-charcoal-gray">This wallet has no transaction history</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {transactions.map((tx) => (
                        <div key={tx.id} className="bg-gray-800/30 border border-gray-800 rounded-xl p-4 hover:bg-gray-800/50 transition-colors group">
                          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-3">
                                {getTransactionTypeBadge(tx.transaction_type)}
                                {getStatusBadge(tx.status)}
                              </div>
                              <div className="space-y-2">
                                <div className="text-xl font-bold text-white">
                                  {AdminAPIUtils.formatBTC(tx.amount)} BTC
                                </div>
                                {tx.description && (
                                  <div className="text-sm text-charcoal-gray">{tx.description}</div>
                                )}
                                <div className="text-xs text-charcoal-gray">
                                  {AdminAPIUtils.formatDate(tx.created_at, 'datetime')}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {tx.bitcoin_tx_hash && (
                                <button
                                  onClick={() => copyToClipboard(tx.bitcoin_tx_hash)}
                                  className="px-3 py-1.5 bg-gray-800/50 hover:bg-gray-700 text-gray-300 hover:text-white rounded-lg transition-all duration-300 flex items-center gap-1.5"
                                  title="Copy TX Hash"
                                >
                                  <Bitcoin className="w-3 h-3" />
                                  <span className="text-xs">Copy Hash</span>
                                </button>
                              )}
                              <div className="relative">
                                <button
                                  onClick={() => setActionMenu(actionMenu === tx.id ? null : tx.id)}
                                  className="p-2 bg-gray-800/50 hover:bg-gray-700 text-charcoal-gray hover:text-white rounded-lg transition-all duration-300"
                                >
                                  <MoreVertical className="w-4 h-4" />
                                </button>
                                {actionMenu === tx.id && (
                                  <div className="absolute right-0 mt-2 w-48 bg-night-blue border border-gray-800 rounded-xl shadow-2xl py-2 z-50">
                                    <div className="px-4 py-2 border-b border-gray-800">
                                      <p className="text-sm font-medium text-white">Transaction Actions</p>
                                    </div>
                                    <button className="w-full px-4 py-2.5 text-sm text-sky-400 hover:bg-sky-500/10 flex items-center gap-2 transition-colors">
                                      <Eye className="w-4 h-4" />
                                      View Details
                                    </button>
                                    <button className="w-full px-4 py-2.5 text-sm text-amber-400 hover:bg-amber-500/10 flex items-center gap-2 transition-colors">
                                      <FileText className="w-4 h-4" />
                                      Export Receipt
                                    </button>
                                    <div className="px-4 py-2 border-t border-gray-800">
                                      <p className="text-xs text-charcoal-gray">TX ID: {tx.id}</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {/* Transaction Pagination */}
                      {transactionPagination.totalPages > 1 && (
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-6 pt-6 border-t border-gray-800">
                          <div className="text-sm text-charcoal-gray">
                            Showing <span className="text-white font-medium">{transactions.length}</span> of{' '}
                            <span className="text-white font-medium">{transactionPagination.total}</span> transactions
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => setTransactionPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                              disabled={transactionPagination.page === 1}
                              className={`px-4 py-2.5 rounded-xl flex items-center gap-2 transition-all duration-300 ${
                                transactionPagination.page === 1
                                  ? 'bg-gray-800/50 text-gray-600 cursor-not-allowed'
                                  : 'bg-gray-800 hover:bg-gray-700 text-white hover:scale-105'
                              }`}
                            >
                              <ChevronLeft className="w-4 h-4" />
                              Previous
                            </button>
                            <div className="flex items-center space-x-1">
                              {[...Array(Math.min(3, transactionPagination.totalPages))].map((_, i) => {
                                let pageNum;
                                if (transactionPagination.totalPages <= 3) {
                                  pageNum = i + 1;
                                } else if (transactionPagination.page <= 2) {
                                  pageNum = i + 1;
                                } else if (transactionPagination.page >= transactionPagination.totalPages - 1) {
                                  pageNum = transactionPagination.totalPages - 2 + i;
                                } else {
                                  pageNum = transactionPagination.page - 1 + i;
                                }
                                
                                return (
                                  <button
                                    key={pageNum}
                                    onClick={() => setTransactionPagination(prev => ({ ...prev, page: pageNum }))}
                                    className={`px-4 py-2.5 min-w-[40px] rounded-xl transition-all duration-300 ${
                                      transactionPagination.page === pageNum
                                        ? 'bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-lg'
                                        : 'bg-gray-800 hover:bg-gray-700 text-white hover:scale-105'
                                    }`}
                                  >
                                    {pageNum}
                                  </button>
                                );
                              })}
                              {transactionPagination.totalPages > 3 && transactionPagination.page < transactionPagination.totalPages - 1 && (
                                <>
                                  <span className="px-2 text-charcoal-gray">...</span>
                                  <button
                                    onClick={() => setTransactionPagination(prev => ({ ...prev, page: transactionPagination.totalPages }))}
                                    className="px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl hover:scale-105 transition-all duration-300"
                                  >
                                    {transactionPagination.totalPages}
                                  </button>
                                </>
                              )}
                            </div>
                            <button
                              onClick={() => setTransactionPagination(prev => ({ ...prev, page: Math.min(transactionPagination.totalPages, prev.page + 1) }))}
                              disabled={transactionPagination.page === transactionPagination.totalPages}
                              className={`px-4 py-2.5 rounded-xl flex items-center gap-2 transition-all duration-300 ${
                                transactionPagination.page === transactionPagination.totalPages
                                  ? 'bg-gray-800/50 text-gray-600 cursor-not-allowed'
                                  : 'bg-gray-800 hover:bg-gray-700 text-white hover:scale-105'
                              }`}
                            >
                              Next
                              <ChevronRight className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'logs' && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      Audit Logs
                    </h3>
                    <button
                      onClick={fetchLogs}
                      className="p-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all duration-300 hover:scale-105"
                    >
                      <RefreshCw className={`w-4 h-4 ${logsLoading ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                  
                  {logsLoading ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sky-500 mx-auto mb-6"></div>
                      <p className="text-charcoal-gray">Loading logs...</p>
                    </div>
                  ) : logs.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText className="w-16 h-16 text-gray-700 mx-auto mb-4" />
                      <p className="text-charcoal-gray">No audit logs found</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {logs.map((log) => (
                        <div key={log.id} className="border-l-4 border-sky-500 pl-4 py-3 bg-gray-800/30 rounded-r-xl">
                          <div className="flex flex-col sm:flex-row sm:justify-between">
                            <div className="font-medium text-white">{log.action}</div>
                            <div className="text-sm text-charcoal-gray">
                              {AdminAPIUtils.timeAgo(log.created_at)}
                            </div>
                          </div>
                          <div className="text-sm text-charcoal-gray mt-1">
                            {log.description || 'No description provided'}
                          </div>
                          {log.admin_name && (
                            <div className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                              <User className="w-3 h-3" />
                              By: {log.admin_name}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'adjust' && (
                <div>
                  <h3 className="text-lg font-bold text-white mb-6">Adjust Balance</h3>
                  <form onSubmit={handleAdjustBalance} className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-charcoal-gray mb-2">
                          Amount in BTC
                        </label>
                        <div className="relative">
                          <Bitcoin className="absolute left-4 top-1/2 transform -translate-y-1/2 text-amber-400 w-5 h-5" />
                          <input
                            type="number"
                            step="0.00000001"
                            placeholder="0.00000000"
                            value={adjustAmount}
                            onChange={(e) => setAdjustAmount(e.target.value)}
                            className="w-full pl-12 pr-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white placeholder-charcoal-gray focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                            required
                          />
                          <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                            {adjustAmount && !isNaN(parseFloat(adjustAmount)) && parseFloat(adjustAmount) > 0 ? (
                              <Plus className="w-4 h-4 text-emerald-400" />
                            ) : adjustAmount && !isNaN(parseFloat(adjustAmount)) && parseFloat(adjustAmount) < 0 ? (
                              <Minus className="w-4 h-4 text-rose-400" />
                            ) : null}
                          </div>
                        </div>
                        <div className="mt-4 p-4 bg-gray-800/30 rounded-xl">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-charcoal-gray">Current Balance</span>
                            <span className="text-emerald-400 font-medium">
                              {AdminAPIUtils.formatBTC(wallet.balance || 0)} BTC
                            </span>
                          </div>
                          {adjustAmount && !isNaN(parseFloat(adjustAmount)) && (
                            <div className="flex justify-between items-center">
                              <span className="text-charcoal-gray">New Balance</span>
                              <span className={`font-bold ${
                                parseFloat(adjustAmount) >= 0 ? 'text-emerald-400' : 'text-rose-400'
                              }`}>
                                {AdminAPIUtils.formatBTC((parseFloat(wallet.balance || 0) + parseFloat(adjustAmount)))} BTC
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-charcoal-gray mb-2">Description</label>
                        <textarea
                          placeholder="Reason for adjustment..."
                          value={adjustDescription}
                          onChange={(e) => setAdjustDescription(e.target.value)}
                          className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white placeholder-charcoal-gray focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors resize-none"
                          rows="5"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="flex justify-end gap-3 pt-6 border-t border-gray-800">
                      <button
                        type="button"
                        onClick={() => setActiveTab('overview')}
                        className="px-5 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all duration-300"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={adjustLoading || !adjustAmount || !adjustDescription}
                        className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-white rounded-xl transition-all duration-300 hover:scale-[1.02] flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {adjustLoading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                            Processing...
                          </>
                        ) : (
                          <>
                            <Bitcoin className="w-4 h-4" />
                            Adjust Balance
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Quick Actions */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
            <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
              <Zap className="w-5 h-5" />
              Quick Actions
            </h3>
            <div className="space-y-3">
              <button className="w-full px-4 py-3.5 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300 hover:scale-[1.02] flex items-center justify-between group">
                <span>Add Funds</span>
                <Plus className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={toggleWalletLock}
                className={`w-full px-4 py-3.5 rounded-xl transition-all duration-300 hover:scale-[1.02] flex items-center justify-between group ${
                  wallet.locked
                    ? 'bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white'
                    : 'bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-600 hover:to-red-700 text-white'
                }`}
              >
                <span>{wallet.locked ? 'Unlock Wallet' : 'Lock Wallet'}</span>
                {wallet.locked ? (
                  <Unlock className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                ) : (
                  <Lock className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                )}
              </button>
              <button
                onClick={() => navigate(`/users/${wallet.user_id}`)}
                className="w-full px-4 py-3.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all duration-300 hover:scale-[1.02] flex items-center justify-between group"
              >
                <span>View User</span>
                <User className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => setActiveTab('transactions')}
                className="w-full px-4 py-3.5 bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700 text-white rounded-xl transition-all duration-300 hover:scale-[1.02] flex items-center justify-between group"
              >
                <span>View Transactions</span>
                <History className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>

          {/* Wallet Details */}
          <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
            <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
              <WalletIcon className="w-5 h-5" />
              Wallet Details
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between py-3 border-b border-gray-800/50">
                <span className="text-sm text-charcoal-gray">Wallet ID</span>
                <span className="font-mono text-sm text-gray-300">{wallet.id}</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-gray-800/50">
                <span className="text-sm text-charcoal-gray">Type</span>
                <div className="flex items-center gap-2">
                  {getWalletTypeBadge(wallet.wallet_type)}
                </div>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-gray-800/50">
                <span className="text-sm text-charcoal-gray">Status</span>
                {getLockStatus(wallet.locked)}
              </div>
              <div className="flex items-center justify-between py-3 border-b border-gray-800/50">
                <span className="text-sm text-charcoal-gray">User ID</span>
                <span className="font-medium text-white">{wallet.user_id}</span>
              </div>
              <div className="flex items-center justify-between py-3">
                <span className="text-sm text-charcoal-gray">Created</span>
                <span className="text-sm text-white">
                  {AdminAPIUtils.formatDate(wallet.created_at, 'date')}
                </span>
              </div>
            </div>
          </div>

          {/* Transaction Summary */}
          <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
            <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Transaction Summary
            </h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-charcoal-gray">Total Volume</span>
                  <span className="text-sm font-bold text-emerald-400">
                    {AdminAPIUtils.formatBTC(
                      (wallet.transaction_stats?.total_deposits || 0) + 
                      (wallet.transaction_stats?.total_withdrawals || 0)
                    )} BTC
                  </span>
                </div>
                <div className="w-full bg-gray-800 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-emerald-500 to-green-600 h-2 rounded-full" 
                    style={{ width: '85%' }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-charcoal-gray">Deposit Ratio</span>
                  <span className="text-sm font-bold text-blue-400">
                    {Math.round(
                      (wallet.transaction_stats?.total_deposits || 0) / 
                      ((wallet.transaction_stats?.total_deposits || 0) + (wallet.transaction_stats?.total_withdrawals || 0) || 1) * 100
                    )}%
                  </span>
                </div>
                <div className="w-full bg-gray-800 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-blue-500 to-cyan-600 h-2 rounded-full" 
                    style={{ width: '70%' }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-charcoal-gray">Avg Transaction</span>
                  <span className="text-sm font-bold text-purple-400">
                    {AdminAPIUtils.formatBTC(
                      ((wallet.transaction_stats?.total_deposits || 0) + (wallet.transaction_stats?.total_withdrawals || 0)) / 
                      (wallet.transaction_stats?.total_transactions || 1)
                    )} BTC
                  </span>
                </div>
                <div className="w-full bg-gray-800 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-purple-500 to-violet-600 h-2 rounded-full" 
                    style={{ width: '45%' }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WalletDetail;